<template>
  <div class="metro-accessibility-chart-container">
    <div 
      ref="container" 
      class="metro-accessibility-chart"
      :class="{ 'loading': !isInViewport || !hasRendered }"
    >
      <div v-if="!isInViewport || !hasRendered" class="chart-placeholder">
        <div class="placeholder-content">
          <div class="placeholder-icon">♿</div>
          <div class="placeholder-text">无障碍设施图表加载中...</div>
        </div>
      </div>
    </div>
    <!-- 数据来源注释 -->
    <div class="data-source">
      数据来源：上海地铁官方网站
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as d3 from 'd3';
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';

// 数据接口定义
interface AccessibilityData {
  line: string
  stations: number
  elevators: number
  liftPlatforms: number
  escalators: number
  total: number
  coverage: number
}

interface ProcessedData extends AccessibilityData {
  lineColor: string
  lineNumber: number
}

const container = ref<HTMLDivElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;
let resizeTimer: number | null = null;
const isInViewport = ref(false);
const hasRendered = ref(false);

// 地铁线路颜色配置 - 与JSON文件保持一致
const lineColors: Record<string, string> = {
  '1号线': '#E3002B',
  '2号线': '#82BF25', 
  '3号线': '#FCD600',
  '4号线': '#461D84',
  '5号线': '#944D9A',
  '6号线': '#D40068',
  '7号线': '#ED6F00',
  '8号线': '#0094D8',
  '9号线': '#87CAED',
  '10号线': '#C6AFD4',
  '11号线': '#871C2B',
  '12号线': '#007A60',
  '13号线': '#E999C0',
  '14号线': '#626020',
  '15号线': '#BCA886',
  '16号线': '#98D1C0',
  '17号线': '#BC796F',
  '18号线': '#C4984F',
  '浦江线': '#B5B5B6',
  '市域机场线': '#008B9A'
}

// 响应式数据
const accessibilityData = ref<AccessibilityData[]>([])

// 解析CSV数据
const parseCSVData = async () => {
  try {
    const response = await fetch('/lift.csv')
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const csvText = await response.text()
    
    const lines = csvText.split('\n')
    const processedData: AccessibilityData[] = []
    
    for (let i = 1; i < lines.length; i++) { // 跳过标题行
      const line = lines[i].trim()
      if (!line) continue
      
      const fields = line.split(',')
      if (fields.length < 4) continue
      
      const lineName = fields[0].trim()
      const stations = parseInt(fields[1].trim()) || 0
      const elevators = parseInt(fields[2].trim()) || 0
      const liftPlatforms = parseInt(fields[3].trim()) || 0
      const escalators = parseInt(fields[4].trim()) || 0
      
      if (lineName && stations > 0) {
        const total = elevators + liftPlatforms + escalators
        const coverage = (total / stations) * 100
        
        processedData.push({
          line: lineName,
          stations,
          elevators,
          liftPlatforms,
          escalators,
          total,
          coverage
        })
      }
    }
    
    if (processedData.length > 0) {
      accessibilityData.value = processedData.sort((a, b) => {
        const getLineNumber = (name: string) => {
          const match = name.match(/(\d+)号线/)
          if (match) return parseInt(match[1])
          if (name === '浦江线') return 100
          if (name === '市域机场线') return 101
          return 999
        }
        return getLineNumber(a.line) - getLineNumber(b.line)
      })
    } else {
      throw new Error('没有解析到有效数据')
    }
    
  } catch (error) {
    // 提供模拟数据作为备用
    accessibilityData.value = [
      { line: '1号线', stations: 28, elevators: 45, liftPlatforms: 13, escalators: 17, total: 75, coverage: 267.9 },
      { line: '2号线', stations: 32, elevators: 70, liftPlatforms: 2, escalators: 1, total: 73, coverage: 228.1 },
      { line: '3号线', stations: 29, elevators: 57, liftPlatforms: 0, escalators: 3, total: 60, coverage: 206.9 }
    ]
  }
}

function render() {
  if (!container.value || !accessibilityData.value.length || !isInViewport.value) return;
  
  // 如果已经渲染过，则不重复渲染动画
  if (hasRendered.value) return;
  hasRendered.value = true;
  
  const element = container.value;
  const width = element.clientWidth;
  const height = 600;
  const margin = { top: 80, right: 120, bottom: 100, left: 90 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  element.innerHTML = '';
  
  const svg = d3.select(element)
    .append('svg')
    .attr('width', width)
    .attr('height', height);
  
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);
  
  // 数据处理
  const processedData: ProcessedData[] = accessibilityData.value.map(d => {
    const getLineNumber = (name: string) => {
      const match = name.match(/(\d+)号线/)
      if (match) return parseInt(match[1])
      if (name === '浦江线') return 100
      if (name === '市域机场线') return 101
      return 999
    }
    
    return {
      ...d,
      lineColor: lineColors[d.line] || '#6b7280',
      lineNumber: getLineNumber(d.line)
    }
  });
  
  // 比例尺
  const xScale = d3.scaleBand()
    .domain(processedData.map(d => d.line))
    .range([0, innerWidth])
    .padding(0.2);
  
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(processedData, d => d.total) || 100])
    .range([innerHeight, 0])
    .nice();
  
  // 堆叠数据处理
  const stackKeys = ['elevators', 'liftPlatforms', 'escalators'];
  const stackData = d3.stack<AccessibilityData>()
    .keys(stackKeys as (keyof AccessibilityData)[])
    (processedData);
  
  // 颜色映射
  const facilityColors = {
    elevators: '#3b82f6',      // 蓝色 - 无障碍电梯
    liftPlatforms: '#10b981',  // 绿色 - 升降平台
    escalators: '#f59e0b'      // 橙色 - 斜挂梯
  };
  
  const facilityNames = {
    elevators: '无障碍电梯',
    liftPlatforms: '升降平台',
    escalators: '斜挂梯'
  };
  
  // 背景网格线
  const yTicks = yScale.ticks(8);
  yTicks.forEach(tick => {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(tick))
      .attr('y2', yScale(tick))
      .attr('stroke', '#f1f5f9')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8);
  });
  
  // 绘制堆叠柱状图
  const layers = g.selectAll('.layer')
    .data(stackData)
    .enter()
    .append('g')
    .attr('class', 'layer')
    .attr('fill', d => facilityColors[d.key as keyof typeof facilityColors]);
  
  const bars = layers.selectAll('.bar')
    .data(d => d)
    .enter()
    .append('rect')
    .attr('class', 'bar')
    .attr('x', d => (xScale(d.data.line) || 0))
    .attr('width', xScale.bandwidth())
    .attr('y', innerHeight)
    .attr('height', 0)
    .attr('rx', 3)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 1)
    .style('cursor', 'pointer')
    .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))')
    .on('mouseenter', function(event, d) {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 2)
        .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))');
      
      showTooltip(d, this, event);
    })
    .on('mouseleave', function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 1)
        .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))');
      
      hideTooltip();
    })
    .transition()
    .duration(1000)
    .delay((d, i) => i * 80)
    .attr('y', d => yScale(d[1]))
    .attr('height', d => Math.max(0, yScale(d[0]) - yScale(d[1])));
  
  // 为每个柱子添加线路颜色边框
  bars.each(function(d) {
    const currentBar = d3.select(this);
    const lineData = processedData.find(item => item.line === d.data.line);
    if (lineData) {
      currentBar.style('border-left', `4px solid ${lineData.lineColor}`);
    }
  });
  
  // Y轴
  const yAxis = d3.axisLeft(yScale)
    .tickFormat(d => `${d}`)
    .tickSize(-innerWidth)
    .tickPadding(15);
  
  g.append('g')
    .attr('class', 'y-axis')
    .call(yAxis)
    .selectAll('text')
    .attr('font-size', '12px')
    .attr('fill', '#6b7280');
  
  g.select('.y-axis').selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.3);
  
  g.select('.y-axis').select('.domain').remove();
  
  // X轴
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => d)
    .tickSizeOuter(0);
  
  const xAxisGroup = g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0, ${innerHeight})`)
    .call(xAxis);
    
  // X轴标签样式 - 使用对应线路颜色
  xAxisGroup.selectAll('text')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', d => {
      const lineData = processedData.find(item => item.line === d);
      return lineData ? lineData.lineColor : '#374151';
    })
    .attr('transform', 'rotate(-45)')
    .style('text-anchor', 'end')
    .attr('dx', '-0.5em')
    .attr('dy', '0.15em');
  
  // X轴线条样式
  xAxisGroup.select('.domain')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-width', 1);
    
  xAxisGroup.selectAll('.tick line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.5);
  
  // Y轴标题
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -70)
    .attr('x', -innerHeight / 2)
    .attr('text-anchor', 'middle')
    .attr('font-size', '14px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('设施数量 (个)');
  
  // 图例
  const legend = g.append('g')
    .attr('transform', `translate(${innerWidth + 20}, 20)`);
  
  const legendItems = Object.entries(facilityNames).map(([key, name], index) => ({
    key,
    name,
    color: facilityColors[key as keyof typeof facilityColors],
    index
  }));
  
  const legendItem = legend.selectAll('.legend-item')
    .data(legendItems)
    .enter()
    .append('g')
    .attr('class', 'legend-item')
    .attr('transform', (d, i) => `translate(0, ${i * 30})`);
  
  legendItem.append('rect')
    .attr('width', 18)
    .attr('height', 18)
    .attr('rx', 3)
    .attr('fill', d => d.color)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 2)
    .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))');
  
  legendItem.append('text')
    .attr('x', 25)
    .attr('y', 9)
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '13px')
    .attr('font-weight', '500')
    .attr('fill', '#374151')
    .text(d => d.name);
  
  // 统计信息显示
  const statsContainer = g.append('g')
    .attr('transform', `translate(${innerWidth + 20}, 120)`);
  
  const totalFacilities = processedData.reduce((sum, d) => sum + d.total, 0);
  const totalStations = processedData.reduce((sum, d) => sum + d.stations, 0);
  const avgCoverage = (totalFacilities / totalStations).toFixed(1);
  
  statsContainer.append('rect')
    .attr('width', 90)
    .attr('height', 80)
    .attr('rx', 8)
    .attr('fill', '#f8fafc')
    .attr('stroke', '#e2e8f0')
    .attr('stroke-width', 1);
  
  statsContainer.append('text')
    .attr('x', 45)
    .attr('y', 20)
    .attr('text-anchor', 'middle')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('整体情况');
  
  statsContainer.append('text')
    .attr('x', 45)
    .attr('y', 40)
    .attr('text-anchor', 'middle')
    .attr('font-size', '18px')
    .attr('font-weight', '700')
    .attr('fill', '#dc2626')
    .text(totalFacilities);
  
  statsContainer.append('text')
    .attr('x', 45)
    .attr('y', 55)
    .attr('text-anchor', 'middle')
    .attr('font-size', '10px')
    .attr('fill', '#6b7280')
    .text('总设施数');
  
  statsContainer.append('text')
    .attr('x', 45)
    .attr('y', 70)
    .attr('text-anchor', 'middle')
    .attr('font-size', '10px')
    .attr('fill', '#6b7280')
    .text(`平均${avgCoverage}个/站`);
  
  // 提示框函数
  function showTooltip(data: any, element: SVGRectElement, event: MouseEvent) {
    const tooltip = d3.select('body').selectAll('.accessibility-tooltip').data([0]);
    
    const tooltipEnter = tooltip.enter()
      .append('div')
      .attr('class', 'accessibility-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px 16px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)')
      .style('backdrop-filter', 'blur(10px)')
      .style('opacity', 0);
    
    const tooltipUpdate = tooltipEnter.merge(tooltip as any);
    
    const lineData = processedData.find(item => item.line === data.data.line);
    const facilityType = Object.keys(facilityColors).find(key => 
      stackData.find(stack => stack.key === key)?.find(d => d === data)
    );
    
    const facilityValue = facilityType ? data.data[facilityType as keyof AccessibilityData] : 0;
    const facilityName = facilityType ? facilityNames[facilityType as keyof typeof facilityNames] : '';
    
    tooltipUpdate
      .html(`
        <div style="font-weight: bold; color: ${lineData?.lineColor}; margin-bottom: 8px;">
          ${data.data.line}
        </div>
        <div style="margin-bottom: 4px;">
          <strong>站点数量：</strong>${data.data.stations}个
        </div>
        <div style="margin-bottom: 4px;">
          <strong>${facilityName}：</strong>${facilityValue}个
        </div>
        <div style="margin-bottom: 4px;">
          <strong>总设施数：</strong>${data.data.total}个
        </div>
        <div>
          <strong>覆盖率：</strong>${data.data.coverage.toFixed(1)}%
        </div>
      `)
      .style('left', (event.pageX + 10) + 'px')
      .style('top', (event.pageY - 10) + 'px')
      .transition()
      .duration(200)
      .style('opacity', 1);
  }
  
  function hideTooltip() {
    d3.select('body').selectAll('.accessibility-tooltip')
      .transition()
      .duration(200)
      .style('opacity', 0)
      .remove();
  }
}

// 生命周期钩子
onMounted(async () => {
  await parseCSVData()
  
  // 设置视口观察器
  if (container.value) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasRendered.value) {
            isInViewport.value = true;
            render();
          }
        });
      },
      {
        threshold: 0.3, // 当30%的元素进入视口时触发
        rootMargin: '50px' // 提前50px触发
      }
    );
    
    intersectionObserver.observe(container.value);
  }
  
  // 响应式调整
  resizeObserver = new ResizeObserver(() => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      if (hasRendered.value) {
        // 如果已经渲染过，允许重新渲染以适应尺寸变化
        const wasRendered = hasRendered.value;
        hasRendered.value = false;
        render();
        hasRendered.value = wasRendered;
      }
    }, 300)
  })
  
  if (container.value) {
    resizeObserver.observe(container.value)
  }
})

// 监听数据变化
watch(accessibilityData, () => {
  if (isInViewport.value) {
    hasRendered.value = false;
    render();
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect()
  }
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
})
</script>

<style scoped>
.metro-accessibility-chart-container {
  position: relative;
  width: 100%;
  height: 600px;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.metro-accessibility-chart {
  width: 100%;
  height: 600px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  /* 确保字体使用思源黑体 */
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
}

.metro-accessibility-chart.loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.placeholder-content {
  text-align: center;
  color: #6b7280;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  animation: pulse 2s infinite;
}

.placeholder-text {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.8;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .metro-accessibility-chart-container {
    height: 500px;
  }
  
  .metro-accessibility-chart {
    height: 500px;
  }
}

@media (max-width: 480px) {
  .metro-accessibility-chart-container {
    height: 450px;
  }
  
  .metro-accessibility-chart {
    height: 450px;
  }
}
</style>